'use client'

import Link from 'next/link'
import {
  getAvailabilityLabel,
  getIntegrationAvailability,
  getIntegrationTypeLabel,
  type IntegrationAvailability,
  type IntegrationMeta,
} from '@/lib/integrations/catalog'
import { TYPE } from '@/lib/ui/typography'
import { IntegrationBrandLogo } from './components/integration-brand-logo'
import type { IntegrationListMode } from './integration-list'
import './integrations.css'
import { cn } from '@brew/ui/cn'

interface IntegrationCardProps {
  integration: IntegrationMeta
  isConnected: boolean
  mode: IntegrationListMode
}

const CARD_APP_CLASS =
  'group bg-background border-border relative flex w-full flex-col gap-4 rounded-lg border p-4 text-left transition-colors duration-150 ease-out'

const CARD_PUBLIC_CLASS =
  'group relative flex w-full flex-col gap-4 rounded-lg border border-border bg-background p-5 text-left transition-colors duration-150 ease-out dark:bg-card'
const CARD_INTERACTIVE_CLASS =
  'cursor-pointer hover:border-foreground/30 focus-visible:border-foreground/40 focus-visible:outline-none'
const CARD_DISABLED_CLASS = 'cursor-not-allowed opacity-65'

const AVAILABILITY_DOT_CLASS: Record<IntegrationAvailability, string> = {
  connected: 'bg-emerald-500',
  coming_soon: 'bg-orange-400',
  available: 'bg-foreground/30',
}

const AVAILABILITY_TEXT_CLASS: Record<IntegrationAvailability, string> = {
  connected: 'text-emerald-700 dark:text-emerald-400',
  coming_soon: 'text-orange-600 dark:text-orange-400',
  available: 'text-muted-foreground',
}

const DIRECTION_LABEL: Record<IntegrationMeta['dataDirection'], string> = {
  data_in: 'In',
  data_out: 'Out',
}

function CardInner({
  integration,
  availability,
}: {
  integration: IntegrationMeta
  availability: IntegrationAvailability
}) {
  const availabilityLabel = getAvailabilityLabel(availability)

  return (
    <>
      <div className="relative z-10 flex items-start gap-3">
        <span className="flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border">
          <IntegrationBrandLogo
            variant="card"
            brandDomain={integration.brandDomain}
            provider={integration.id}
            name={integration.name}
            tone={integration.logoTone}
            background={integration.logoBackground}
            className="h-full w-full"
            imgClassName="h-full w-full rounded-lg object-cover"
          />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className={cn(TYPE.bodyMedium, 'truncate leading-tight')}>
            {integration.name}
          </span>
          <p className={cn(TYPE.supporting, 'line-clamp-2 leading-snug')}>
            {integration.shortDescription}
          </p>
        </div>
      </div>
      <div
        className={cn(
          TYPE.caption,
          'relative z-10 flex items-center justify-between gap-2'
        )}
      >
        <span className="flex items-center gap-2">
          <span>{getIntegrationTypeLabel(integration.integrationType)}</span>
          <span className="text-muted-foreground/40" aria-hidden>
            ·
          </span>
          <span>{DIRECTION_LABEL[integration.dataDirection]}</span>
        </span>
        <span
          className={cn(
            'flex items-center gap-1.5',
            AVAILABILITY_TEXT_CLASS[availability]
          )}
        >
          <span
            className={cn(
              'size-1.5 shrink-0 rounded-full',
              AVAILABILITY_DOT_CLASS[availability]
            )}
            aria-hidden
          />
          {availabilityLabel}
        </span>
      </div>
    </>
  )
}

export function IntegrationCard({
  integration,
  isConnected,
  mode,
}: IntegrationCardProps) {
  const availability = getIntegrationAvailability(integration, isConnected)
  const ariaLabel = `${integration.name} — ${getAvailabilityLabel(availability)}`

  const cardBase = mode === 'public' ? CARD_PUBLIC_CLASS : CARD_APP_CLASS

  if (integration.availability === 'coming_soon') {
    return (
      <div
        className={cn(cardBase, CARD_DISABLED_CLASS)}
        title={`${integration.name} — coming soon`}
      >
        <CardInner integration={integration} availability={availability} />
      </div>
    )
  }

  // Both modes navigate to the dedicated page. The public page renders
  // a "Sign in to connect" CTA via Clerk; the app page wires the live
  // brand-scoped Convex state. Auth-aware redirects in `proxy.ts` swap
  // the URL prefix in either direction.
  const href =
    mode === 'public'
      ? `/browse/integrations/${integration.id}`
      : `/integrations/${integration.id}`

  return (
    <Link
      href={href}
      prefetch
      aria-label={ariaLabel}
      className={cn(cardBase, CARD_INTERACTIVE_CLASS)}
    >
      <CardInner integration={integration} availability={availability} />
    </Link>
  )
}
